import { promiseState } from '@passionware/platform-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from 'v5/design-system/Form';
import { Form } from 'v5/design-system/Form';
import { Input } from 'v5/design-system/Input';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { WithServices } from 'v5/platform/ts/services';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { WithUploadService } from 'v5/services/io/UploadService/UploadService';
import { EnterpriseIconUploadField } from './EnterpriseIconUploadField';

export type EnterpriseWorkspaceFormData = {
  name: string;
  iconUrl: string;
};

export type EnterpriseWorkspaceDialogContentProps = WithServices<
  [WithUploadService, WithToastService]
> & {
  workspace?: EnterpriseWorkspace;
  open?: boolean;
  onSubmit?: (data: EnterpriseWorkspaceFormData) => Promise<void> | void;
};

export function EnterpriseWorkspaceDialogContent({
  workspace,
  open = true,
  onSubmit,
  services
}: EnterpriseWorkspaceDialogContentProps) {
  const isEdit = workspace != null;
  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });
  const mutation = promiseState.useMutation(
    async (data: EnterpriseWorkspaceFormData) => onSubmit?.(data)
  );

  const form = useForm<EnterpriseWorkspaceFormData>({
    defaultValues: workspace
      ? {
          name: workspace.name,
          iconUrl: workspace.iconUrl
        }
      : {
          name: '',
          iconUrl: ''
        }
  });

  useRegisterDirtyState(form.formState.isDirty);

  const name = form.watch('name');
  const previewFallback = name || workspace?.name || t('createWorkspace');

  return (
    <Form {...form}>
      <FormDialogContent
        open={open}
        title={t(isEdit ? 'editWorkspace' : 'createWorkspace')}
        description={t(
          isEdit ? 'editWorkspaceDescription' : 'createWorkspaceDescription'
        )}
        onSubmit={form.handleSubmit(data => mutation.track(data))}
        mutation={mutation.state}
        saveText={t(isEdit ? 'save' : 'create')}
        disabled={!name?.trim()}
      >
        <FormField
          control={form.control}
          name="name"
          rules={{ required: true }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fieldName')}</FormLabel>
              <FormControl>
                <Input {...field} autoFocus />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="iconUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fieldIcon')}</FormLabel>
              <FormControl>
                <EnterpriseIconUploadField
                  services={services}
                  value={field.value}
                  onChange={field.onChange}
                  previewName={previewFallback}
                  variant="workspace"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </FormDialogContent>
    </Form>
  );
}
